import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Heart, Plus, Search, Edit2, Loader2, LayoutGrid, List, Trash2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { TimelineEntry } from '../../types';
import TimelineModal from '../../components/common/TimelineModal';

type ViewMode = 'card' | 'list';

const TimelineScreen: React.FC = () => {
  const { entriesWithCounts, addEntry, updateEntry, deleteEntry, isLoading, user, coupleId } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimelineEntry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('timelineViewMode');
    return (saved as ViewMode) || 'card';
  });

  const isSoloMode = useMemo(() => {
    return !coupleId || (user && coupleId === user.id);
  }, [coupleId, user]);

  useEffect(() => {
    localStorage.setItem('timelineViewMode', viewMode);
  }, [viewMode]);

  const filteredEntries = useMemo(() => {
    const sorted = [...entriesWithCounts].reverse();
    if (!searchTerm.trim()) return sorted;
    
    const lowerSearch = searchTerm.toLowerCase();
    return sorted.filter(entry => 
      entry.title.toLowerCase().includes(lowerSearch) ||
      entry.content.toLowerCase().includes(lowerSearch) ||
      entry.tags.some(tag => tag.toLowerCase().includes(lowerSearch))
    );
  }, [entriesWithCounts, searchTerm]);

  const handleOpenAddModal = () => {
    setEditingEntry(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (entry: TimelineEntry) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('정말로 이 추억을 삭제하시겠습니까?')) {
      await deleteEntry(id);
    }
  };

  const handleSave = async (entryData: Omit<TimelineEntry, 'id' | 'meetingCount'>) => {
    if (editingEntry) {
      await updateEntry(editingEntry.id, entryData);
    } else {
      await addEntry(entryData);
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-24 relative">
      <header className="flex justify-between items-center py-4">
        <div className="flex flex-col text-left">
          <h1 className="text-2xl font-bold text-charcoal">우리의 타임라인</h1>
          {isSoloMode && (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="text-[10px] font-black text-primary uppercase tracking-wider">혼자 기록 중</span>
            </div>
          )}
        </div>
        <div className="flex bg-secondary p-1 rounded-xl">
          <button 
            onClick={() => setViewMode('card')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'card' ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}
          >
            <LayoutGrid size={18} />
          </button>
          <button 
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}
          >
            <List size={18} />
          </button>
        </div>
      </header>

      {/* Search Bar */}
      <div className="relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors" size={18} />
        <input 
          type="text" 
          placeholder="제목, 내용, 해시태그 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-white border border-secondary rounded-2xl pl-12 pr-4 py-3.5 text-sm focus:ring-2 focus:ring-primary outline-none transition-all shadow-sm"
        />
      </div>

      {/* Floating Action Button */}
      <button 
        onClick={handleOpenAddModal}
        className="fixed bottom-24 right-6 bg-primary text-white p-4 rounded-full shadow-2xl active:scale-95 transition-all z-40"
      >
        <Plus size={24} strokeWidth={3} />
      </button>

      <div className={`flex flex-col ${viewMode === 'card' ? 'gap-10' : 'gap-4'}`}>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Loader2 className="animate-spin mb-4" size={32} />
            <p className="text-sm font-medium">추억을 불러오는 중...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Heart size={40} className="mx-auto mb-4 opacity-20" />
            <p className="font-medium text-sm">
              {searchTerm ? '검색 결과가 없어요.' : '아직 기록된 추억이 없어요.'}
            </p>
            <p className="text-xs mt-1">
              {searchTerm ? '다른 검색어를 입력해 보세요.' : '상단의 + 버튼을 눌러 첫 기록을 남겨보세요!'}
            </p>
          </div>
        ) : (
          filteredEntries.map((entry, index) => {
            const hasImage = entry.image_urls && entry.image_urls.length > 0;
            
            return (
              <motion.article 
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex flex-col group ${viewMode === 'list' ? 'bg-white p-4 rounded-[24px] border border-secondary shadow-sm' : 'gap-3'}`}
              >
                {/* Meta Info (Card: Top, List: Inside) */}
                <div className={`flex justify-between items-center ${viewMode === 'list' ? 'mb-2' : 'px-1'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold text-gray-400 tracking-tight">
                      {entry.event_date.replace(/-/g, '.')}
                    </span>
                    {entry.meetingCount && (
                      <span className="text-[9px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-black uppercase tracking-tighter">
                        {entry.meetingCount}번째 만남
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => handleOpenEditModal(entry)}
                      className="text-gray-300 hover:text-primary transition-colors p-1"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button 
                      onClick={() => handleDelete(entry.id)}
                      className="text-gray-300 hover:text-red-500 transition-colors p-1"
                    >
                      <Trash2 size={15} />
                    </button>
                    <button className="text-gray-300 hover:text-primary transition-colors p-1">
                      <Heart size={16} />
                    </button>
                  </div>
                </div>
                
                {/* Layout Container */}
                <div className={`${viewMode === 'list' ? 'flex gap-4' : 'flex flex-col gap-3'}`}>
                  {/* Image Area - Carousel for Card, Single for List */}
                  {hasImage && (
                    viewMode === 'card' ? (
                      <div className="relative w-full overflow-hidden rounded-[32px] group/carousel">
                        <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-1">
                          {entry.image_urls.map((url, i) => (
                            <div key={i} className="flex-none w-full aspect-square snap-center bg-secondary">
                              <img 
                                src={url} 
                                alt={`${entry.title} ${i + 1}`} 
                                className="w-full h-full object-cover" 
                              />
                            </div>
                          ))}
                        </div>
                        {entry.image_urls.length > 1 && (
                          <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] text-white font-black">
                            1 / {entry.image_urls.length}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-[20px] bg-secondary relative shrink-0 w-20 h-20">
                        <img 
                          src={entry.image_urls[0]} 
                          alt={entry.title} 
                          className="w-full h-full object-cover" 
                        />
                        {entry.image_urls.length > 1 && (
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center text-white text-[10px] font-black">
                            +{entry.image_urls.length - 1}
                          </div>
                        )}
                      </div>
                    )
                  )}

                  {/* Content Area */}
                  <div className={`text-left flex-1 flex flex-col ${viewMode === 'card' ? 'px-2' : ''}`}>
                    <h2 className={`font-black text-charcoal ${viewMode === 'card' ? 'text-lg' : 'text-sm'}`}>
                      {entry.title}
                    </h2>
                    {entry.location && (
                      <div className="flex items-center gap-1 text-gray-400 text-[11px] mt-1 font-medium">
                        <MapPin size={10} className="text-primary/50" />
                        <span>{entry.location}</span>
                      </div>
                    )}
                    <p className={`text-charcoal/70 leading-relaxed font-medium whitespace-pre-wrap mt-2 ${
                      viewMode === 'card' ? 'text-[13px] mt-3' : 'text-[11px] line-clamp-2'
                    }`}>
                      {entry.content}
                    </p>
                    {viewMode === 'card' && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {entry.tags.map(tag => (
                          <span key={tag} className="text-[10px] text-primary font-bold bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })
        )}
      </div>

      <TimelineModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editingEntry={editingEntry}
      />
    </div>
  );
};

export default TimelineScreen;
